import React, { useState, useEffect, useContext } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { AuthContext, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, Package, ShoppingCart, Truck, CheckCircle, Clock, 
  XCircle, AlertTriangle, Pencil, Trash2, Eye, PackageCheck,
  Box, ArrowRight, Search
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const CATEGORIES = [
  'Supplies', 'Technology', 'Books', 'Furniture', 
  'Uniforms', 'Stationery', 'Educational Materials'
];

const STATUS_COLORS = {
  'Pending': 'bg-yellow-100 text-yellow-800',
  'Approved': 'bg-blue-100 text-blue-800',
  'Processing': 'bg-purple-100 text-purple-800',
  'Shipped': 'bg-indigo-100 text-indigo-800',
  'Delivered': 'bg-green-100 text-green-800',
  'Rejected': 'bg-red-100 text-red-800',
  'Cancelled': 'bg-gray-100 text-gray-800'
};

const STATUS_ICONS = {
  'Pending': Clock,
  'Approved': CheckCircle,
  'Processing': Package,
  'Shipped': Truck,
  'Delivered': PackageCheck,
  'Rejected': XCircle,
  'Cancelled': XCircle
};

const ProcurementManagement = () => {
  const { token, user } = useContext(AuthContext);
  
  // Helper to check if user is a franchisee role
  const isFranchiseeRole = ['FRANCHISEE', 'FRANCHISEE_OPERATOR', 'FRANCHISEE_MANAGER', 'FRANCHISEE_SUPERVISOR'].includes(user?.role);
  const isHQRole = ['HQ_ADMIN', 'SYSTEM_ADMIN'].includes(user?.role);
  
  const [activeTab, setActiveTab] = useState(isHQRole ? 'catalog' : 'shop');
  const [products, setProducts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Dialogs
  const [productDialog, setProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [cartDialog, setCartDialog] = useState(false);
  const [orderDetailDialog, setOrderDetailDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [shippingDialog, setShippingDialog] = useState(false);
  const [stockDialog, setStockDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Form data
  const [productForm, setProductForm] = useState({
    name: '', code: '', category: '', description: '',
    unit_price: 0, unit: 'piece', stock_quantity: 0, min_stock_level: 10
  });
  const [shippingForm, setShippingForm] = useState({
    status: '', courier: '', tracking_number: '', estimated_delivery: '', notes: ''
  });
  const [stockForm, setStockForm] = useState({ quantity_change: 0, reason: 'Restock' });
  const [shippingAddress, setShippingAddress] = useState('');

  useEffect(() => {
    fetchProducts();
    fetchRequests();
  }, [token]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to fetch products');
    }
  };

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/procurement/requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data);
    } catch (error) {
      toast.error('Failed to fetch purchase requests');
    } finally {
      setLoading(false);
    }
  };

  // Product Management Functions (Admin)
  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await axios.patch(`${API}/products/${editingProduct.product_id}`, productForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API}/products`, productForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product created successfully');
      }
      setProductDialog(false);
      setEditingProduct(null);
      resetProductForm();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API}/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product deleted successfully');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleStockUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/products/${selectedProduct.product_id}/stock`, stockForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Stock updated successfully');
      setStockDialog(false);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update stock');
    }
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      code: product.code,
      category: product.category,
      description: product.description || '',
      unit_price: product.unit_price,
      unit: product.unit,
      stock_quantity: product.stock_quantity,
      min_stock_level: product.min_stock_level
    });
    setProductDialog(true);
  };

  const openStockDialog = (product) => {
    setSelectedProduct(product);
    setStockForm({ quantity_change: 0, reason: 'Restock' });
    setStockDialog(true);
  };

  const resetProductForm = () => {
    setProductForm({
      name: '', code: '', category: '', description: '',
      unit_price: 0, unit: 'piece', stock_quantity: 0, min_stock_level: 10
    });
  };

  // Shopping Cart Functions (Franchisee)
  const addToCart = (product) => {
    const existing = cart.find(item => item.product_id === product.product_id);
    if (existing) {
      setCart(cart.map(item => 
        item.product_id === product.product_id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
    toast.success(`${product.name} added to cart`);
  };

  const updateCartQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      setCart(cart.filter(item => item.product_id !== productId));
    } else {
      setCart(cart.map(item => 
        item.product_id === productId ? { ...item, quantity } : item
      ));
    }
  };

  const getCartTotal = () => {
    return cart.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }
    try {
      await axios.post(`${API}/procurement/requests`, {
        franchisee_id: user.franchisee_id,
        items: cart.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity
        })),
        total_amount: getCartTotal(),
        requested_by: user.email,
        shipping_address: shippingAddress
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order placed successfully!');
      setCart([]);
      setCartDialog(false);
      setShippingAddress('');
      fetchRequests();
      setActiveTab('orders');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place order');
    }
  };

  // Order Management Functions (Admin)
  const handleApproveReject = async (requestId, status) => {
    try {
      await axios.patch(`${API}/procurement/requests/${requestId}/approve`, {
        status,
        updated_by: user.email,
        notes: status === 'Approved' ? 'Order approved for processing' : 'Order rejected'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Order ${status.toLowerCase()}`);
      fetchRequests();
      fetchProducts(); // Refresh stock
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order');
    }
  };

  const handleShippingUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/procurement/requests/${selectedOrder.request_id}/shipping`, {
        ...shippingForm,
        updated_by: user.email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Order status updated to ${shippingForm.status}`);
      setShippingDialog(false);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update shipping');
    }
  };

  const handleCancelOrder = async (requestId) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    try {
      await axios.patch(`${API}/procurement/requests/${requestId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order cancelled');
      fetchRequests();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel order');
    }
  };

  const openOrderDetail = (order) => {
    setSelectedOrder(order);
    setOrderDetailDialog(true);
  };

  const openShippingDialog = (order, nextStatus) => {
    setSelectedOrder(order);
    setShippingForm({
      status: nextStatus,
      courier: order.shipping?.courier || '',
      tracking_number: order.shipping?.tracking_number || '',
      estimated_delivery: order.shipping?.estimated_delivery || '',
      notes: ''
    });
    setShippingDialog(true);
  };

  // Filtered Data
  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         p.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || p.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const filteredRequests = requests.filter(r => {
    return statusFilter === 'all' || r.status === statusFilter;
  });

  const lowStockProducts = products.filter(p => p.stock_quantity <= p.min_stock_level && p.is_active);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="procurement-management-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-heading font-bold">Procurement & Inventory</h1>
            <p className="text-muted-foreground mt-1">
              {isHQRole ? 'Manage products, inventory, and orders' : 'Browse catalog and track your orders'}
            </p>
          </div>
          {isFranchiseeRole && cart.length > 0 && (
            <Button onClick={() => setCartDialog(true)} data-testid="view-cart-btn">
              <ShoppingCart className="h-4 w-4 mr-2" />
              Cart ({cart.length}) - RM {getCartTotal().toFixed(2)}
            </Button>
          )}
        </div>

        {/* Low Stock Alert for Admin */}
        {isHQRole && lowStockProducts.length > 0 && (
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-orange-700">
                <AlertTriangle className="h-5 w-5" />
                <span className="font-medium">Low Stock Alert: {lowStockProducts.length} products need restocking</span>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            {isHQRole && <TabsTrigger value="catalog">Product Catalog</TabsTrigger>}
            {isFranchiseeRole && <TabsTrigger value="shop">Shop</TabsTrigger>}
            <TabsTrigger value="orders">Orders</TabsTrigger>
            {isHQRole && <TabsTrigger value="inventory">Inventory</TabsTrigger>}
          </TabsList>

          {/* Product Catalog Tab (Admin) */}
          {isHQRole && (
            <TabsContent value="catalog" className="space-y-4">
              <div className="flex justify-between items-center gap-4">
                <div className="flex gap-2 flex-1">
                  <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input 
                      placeholder="Search products..." 
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={() => { resetProductForm(); setEditingProduct(null); setProductDialog(true); }}>
                  <Plus className="h-4 w-4 mr-2" /> Add Product
                </Button>
              </div>

              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Code</TableHead>
                        <TableHead>Product Name</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Unit Price</TableHead>
                        <TableHead>Stock</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredProducts.map((product) => (
                        <TableRow key={product.product_id}>
                          <TableCell className="font-mono text-sm">{product.code}</TableCell>
                          <TableCell className="font-medium">{product.name}</TableCell>
                          <TableCell>{product.category}</TableCell>
                          <TableCell>RM {product.unit_price.toFixed(2)}/{product.unit}</TableCell>
                          <TableCell>
                            <span className={product.stock_quantity <= product.min_stock_level ? 'text-orange-600 font-medium' : ''}>
                              {product.stock_quantity}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge variant={product.is_active ? 'default' : 'secondary'}>
                              {product.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openStockDialog(product)}>
                                <Box className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => openEditProduct(product)}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDeleteProduct(product.product_id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Shop Tab (Franchisee) */}
          {isFranchiseeRole && (
            <TabsContent value="shop" className="space-y-4">
              <div className="flex gap-2">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input 
                    placeholder="Search products..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredProducts.filter(p => p.is_active && p.stock_quantity > 0).map((product) => (
                  <Card key={product.product_id} className="overflow-hidden">
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <Badge variant="outline">{product.category}</Badge>
                        <span className="text-xs text-muted-foreground">{product.code}</span>
                      </div>
                      <CardTitle className="text-lg mt-2">{product.name}</CardTitle>
                      <CardDescription className="line-clamp-2">{product.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-2xl font-bold text-primary">RM {product.unit_price.toFixed(2)}</span>
                        <span className="text-sm text-muted-foreground">per {product.unit}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">{product.stock_quantity} in stock</span>
                        <Button size="sm" onClick={() => addToCart(product)}>
                          <ShoppingCart className="h-4 w-4 mr-1" /> Add
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          )}

          {/* Orders Tab */}
          <TabsContent value="orders" className="space-y-4">
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {Object.keys(STATUS_COLORS).map(status => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order ID</TableHead>
                      {isHQRole && <TableHead>Franchisee</TableHead>}
                      <TableHead>Items</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRequests.map((request) => {
                      const StatusIcon = STATUS_ICONS[request.status];
                      return (
                        <TableRow key={request.request_id}>
                          <TableCell className="font-mono text-sm">{request.request_id.substring(0, 8)}...</TableCell>
                          {isHQRole && (
                            <TableCell>{request.franchisee_id?.substring(0, 8)}...</TableCell>
                          )}
                          <TableCell>{request.items?.length || 0} items</TableCell>
                          <TableCell className="font-medium">RM {request.total_amount?.toFixed(2)}</TableCell>
                          <TableCell>
                            <Badge className={STATUS_COLORS[request.status]}>
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {request.status}
                            </Badge>
                          </TableCell>
                          <TableCell>{new Date(request.requested_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openOrderDetail(request)}>
                                <Eye className="h-4 w-4" />
                              </Button>
                              {isHQRole && request.status === 'Pending' && (
                                <>
                                  <Button size="sm" variant="outline" className="text-green-600" onClick={() => handleApproveReject(request.request_id, 'Approved')}>
                                    Approve
                                  </Button>
                                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleApproveReject(request.request_id, 'Rejected')}>
                                    Reject
                                  </Button>
                                </>
                              )}
                              {isHQRole && request.status === 'Approved' && (
                                <Button size="sm" variant="outline" onClick={() => openShippingDialog(request, 'Processing')}>
                                  Process
                                </Button>
                              )}
                              {isHQRole && request.status === 'Processing' && (
                                <Button size="sm" variant="outline" onClick={() => openShippingDialog(request, 'Shipped')}>
                                  Ship
                                </Button>
                              )}
                              {isHQRole && request.status === 'Shipped' && (
                                <Button size="sm" variant="outline" onClick={() => openShippingDialog(request, 'Delivered')}>
                                  Deliver
                                </Button>
                              )}
                              {isFranchiseeRole && ['Pending', 'Approved'].includes(request.status) && (
                                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleCancelOrder(request.request_id)}>
                                  Cancel
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Inventory Tab (Admin) */}
          {isHQRole && (
            <TabsContent value="inventory" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Total Products</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold">{products.length}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Low Stock Items</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-orange-600">{lowStockProducts.length}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Pending Orders</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-blue-600">
                      {requests.filter(r => r.status === 'Pending').length}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {lowStockProducts.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-orange-500" />
                      Low Stock Products
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Code</TableHead>
                          <TableHead>Product</TableHead>
                          <TableHead>Current Stock</TableHead>
                          <TableHead>Min Level</TableHead>
                          <TableHead>Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {lowStockProducts.map((product) => (
                          <TableRow key={product.product_id}>
                            <TableCell className="font-mono">{product.code}</TableCell>
                            <TableCell>{product.name}</TableCell>
                            <TableCell className="text-orange-600 font-medium">{product.stock_quantity}</TableCell>
                            <TableCell>{product.min_stock_level}</TableCell>
                            <TableCell>
                              <Button size="sm" onClick={() => openStockDialog(product)}>
                                Restock
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          )}
        </Tabs>

        {/* Product Form Dialog */}
        <Dialog open={productDialog} onOpenChange={setProductDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleProductSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Product Code*</Label>
                  <Input value={productForm.code} onChange={(e) => setProductForm({...productForm, code: e.target.value})} required />
                </div>
                <div className="space-y-2">
                  <Label>Category*</Label>
                  <Select value={productForm.category} onValueChange={(v) => setProductForm({...productForm, category: v})}>
                    <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Product Name*</Label>
                <Input value={productForm.name} onChange={(e) => setProductForm({...productForm, name: e.target.value})} required />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={productForm.description} onChange={(e) => setProductForm({...productForm, description: e.target.value})} rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Unit Price (RM)*</Label>
                  <Input type="number" step="0.01" value={productForm.unit_price} onChange={(e) => setProductForm({...productForm, unit_price: parseFloat(e.target.value)})} required />
                </div>
                <div className="space-y-2">
                  <Label>Unit*</Label>
                  <Select value={productForm.unit} onValueChange={(v) => setProductForm({...productForm, unit: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['piece', 'set', 'kit', 'book', 'unit', 'bundle', 'pack'].map(u => (
                        <SelectItem key={u} value={u}>{u}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Initial Stock*</Label>
                  <Input type="number" value={productForm.stock_quantity} onChange={(e) => setProductForm({...productForm, stock_quantity: parseInt(e.target.value)})} required />
                </div>
                <div className="space-y-2">
                  <Label>Min Stock Level*</Label>
                  <Input type="number" value={productForm.min_stock_level} onChange={(e) => setProductForm({...productForm, min_stock_level: parseInt(e.target.value)})} required />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setProductDialog(false)}>Cancel</Button>
                <Button type="submit">{editingProduct ? 'Save Changes' : 'Add Product'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Stock Update Dialog */}
        <Dialog open={stockDialog} onOpenChange={setStockDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Stock - {selectedProduct?.name}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleStockUpdate} className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm">Current Stock: <span className="font-bold">{selectedProduct?.stock_quantity}</span></p>
              </div>
              <div className="space-y-2">
                <Label>Quantity Change*</Label>
                <Input 
                  type="number" 
                  value={stockForm.quantity_change} 
                  onChange={(e) => setStockForm({...stockForm, quantity_change: parseInt(e.target.value)})} 
                  placeholder="Enter positive to add, negative to subtract"
                  required 
                />
              </div>
              <div className="space-y-2">
                <Label>Reason*</Label>
                <Select value={stockForm.reason} onValueChange={(v) => setStockForm({...stockForm, reason: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Restock">Restock</SelectItem>
                    <SelectItem value="Adjustment">Adjustment</SelectItem>
                    <SelectItem value="Return">Return</SelectItem>
                    <SelectItem value="Damaged">Damaged</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setStockDialog(false)}>Cancel</Button>
                <Button type="submit">Update Stock</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Cart Dialog */}
        <Dialog open={cartDialog} onOpenChange={setCartDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Shopping Cart</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {cart.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Your cart is empty</p>
              ) : (
                <>
                  {cart.map((item) => (
                    <div key={item.product_id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">{item.name}</p>
                        <p className="text-sm text-muted-foreground">RM {item.unit_price.toFixed(2)} x {item.quantity}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => updateCartQuantity(item.product_id, item.quantity - 1)}>-</Button>
                        <span className="w-8 text-center">{item.quantity}</span>
                        <Button variant="outline" size="sm" onClick={() => updateCartQuantity(item.product_id, item.quantity + 1)}>+</Button>
                      </div>
                    </div>
                  ))}
                  <div className="space-y-2">
                    <Label>Shipping Address</Label>
                    <Textarea 
                      value={shippingAddress} 
                      onChange={(e) => setShippingAddress(e.target.value)} 
                      placeholder="Enter delivery address..."
                      rows={2}
                    />
                  </div>
                  <div className="p-4 bg-muted rounded-lg">
                    <div className="flex justify-between text-lg font-bold">
                      <span>Total:</span>
                      <span>RM {getCartTotal().toFixed(2)}</span>
                    </div>
                  </div>
                  <Button className="w-full" onClick={handleCheckout}>
                    Place Order
                  </Button>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Order Detail Dialog */}
        <Dialog open={orderDetailDialog} onOpenChange={setOrderDetailDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Order Details</DialogTitle>
            </DialogHeader>
            {selectedOrder && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Order ID</p>
                    <p className="font-mono">{selectedOrder.request_id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    <Badge className={STATUS_COLORS[selectedOrder.status]}>{selectedOrder.status}</Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Order Date</p>
                    <p>{new Date(selectedOrder.requested_at).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Amount</p>
                    <p className="font-bold">RM {selectedOrder.total_amount?.toFixed(2)}</p>
                  </div>
                </div>

                {/* Order Timeline */}
                <div>
                  <p className="font-medium mb-2">Order Timeline</p>
                  <div className="space-y-2">
                    {selectedOrder.status_history?.map((entry, idx) => (
                      <div key={idx} className="flex items-start gap-3 text-sm">
                        <div className="w-2 h-2 rounded-full bg-primary mt-1.5" />
                        <div>
                          <p className="font-medium">{entry.status}</p>
                          <p className="text-muted-foreground">{new Date(entry.updated_at).toLocaleString()}</p>
                          {entry.notes && <p className="text-muted-foreground">{entry.notes}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Shipping Info */}
                {selectedOrder.shipping && (selectedOrder.shipping.courier || selectedOrder.shipping.tracking_number) && (
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="font-medium mb-2">Shipping Information</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {selectedOrder.shipping.courier && (
                        <div><span className="text-muted-foreground">Courier:</span> {selectedOrder.shipping.courier}</div>
                      )}
                      {selectedOrder.shipping.tracking_number && (
                        <div><span className="text-muted-foreground">Tracking:</span> {selectedOrder.shipping.tracking_number}</div>
                      )}
                      {selectedOrder.shipping.estimated_delivery && (
                        <div><span className="text-muted-foreground">Est. Delivery:</span> {selectedOrder.shipping.estimated_delivery}</div>
                      )}
                      {selectedOrder.shipping.actual_delivery && (
                        <div><span className="text-muted-foreground">Delivered:</span> {selectedOrder.shipping.actual_delivery}</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Order Items */}
                <div>
                  <p className="font-medium mb-2">Items</p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product</TableHead>
                        <TableHead>Qty</TableHead>
                        <TableHead>Unit Price</TableHead>
                        <TableHead>Subtotal</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedOrder.items?.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{item.product_name}</p>
                              <p className="text-xs text-muted-foreground">{item.product_code}</p>
                            </div>
                          </TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell>RM {item.unit_price?.toFixed(2)}</TableCell>
                          <TableCell>RM {item.subtotal?.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Shipping Update Dialog */}
        <Dialog open={shippingDialog} onOpenChange={setShippingDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Order Status to {shippingForm.status}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleShippingUpdate} className="space-y-4">
              {shippingForm.status === 'Shipped' && (
                <>
                  <div className="space-y-2">
                    <Label>Courier*</Label>
                    <Input 
                      value={shippingForm.courier} 
                      onChange={(e) => setShippingForm({...shippingForm, courier: e.target.value})} 
                      placeholder="e.g., J&T, PosLaju, DHL"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tracking Number*</Label>
                    <Input 
                      value={shippingForm.tracking_number} 
                      onChange={(e) => setShippingForm({...shippingForm, tracking_number: e.target.value})} 
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Estimated Delivery Date</Label>
                    <Input 
                      type="date"
                      value={shippingForm.estimated_delivery} 
                      onChange={(e) => setShippingForm({...shippingForm, estimated_delivery: e.target.value})} 
                    />
                  </div>
                </>
              )}
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea 
                  value={shippingForm.notes} 
                  onChange={(e) => setShippingForm({...shippingForm, notes: e.target.value})} 
                  rows={2}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShippingDialog(false)}>Cancel</Button>
                <Button type="submit">Update Status</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ProcurementManagement;
